<template>
  <Template.Define>
    <div class="flex items-center gap-3">
      <div
        :class="[
          `flex shrink-0 items-center`,
          item.type === 'page' || item.type === 'special_action'
            ? `text-muted-foreground`
            : ` text-muted-foreground`,
        ]"
      >
        <component
          :is="item.brandIcon"
          v-if="item.type === 'page' && item.brandIcon"
          class="size-4"
        />
        <component :is="icon" v-else-if="icon" class="size-4" />
      </div>

      <div class="my-0.5 grow truncate">
        <div class="text-sm font-medium">
          <span v-html="highlightedText"></span>
        </div>
      </div>

      <div
        v-if="isRouteActive"
        class="text-xs text-muted-foreground opacity-50"
      >
        You are here
      </div>

      <span
        class="mt-0.5 text-right text-[0.8125rem] text-muted-foreground empty:hidden"
      >
        <div v-if="item.matchText">
          <span v-html="highlightedMatchText"></span>
        </div>
        <div v-else-if="item.type === 'stripe_customer'">
          {{ item.obj.customer_id }}
        </div>
        <div v-else-if="item.type === 'stripe_subscription'">
          {{ item.obj.subscription_id }}
        </div>
        <Value.Datetime
          v-else-if="item.type === 'email'"
          :value="item.obj.publish_date || item.obj.creation_date"
        />
        <Value.Datetime
          v-else-if="
            item.type === 'historical_email' ||
            item.type === 'subscriber' ||
            item.type === 'automation' ||
            item.type === 'comment' ||
            item.type === 'note'
          "
          :value="item.obj.creation_date"
        />
      </span>
    </div>
  </Template.Define>

  <ComboboxItem
    ref="itemRef"
    :value="action"
    :class="[
      `-mx-2 select-none scroll-m-3 rounded-md px-1.5 py-0.5 text-foreground transition-all duration-75 data-[highlighted]:bg-muted`,
      nested && `ml-5`,
    ]"
    @pointerenter="prefetchSubscriber"
  >
    <RouterLink
      v-if="action.type === 'route'"
      :to="action.route"
      class="block text-foreground"
    >
      <Template.Use />
    </RouterLink>
    <div v-else>
      <Template.Use />
    </div>
    <div v-if="item.type === 'note'" class="px-7 text-sm text-muted-foreground">
      <span v-html="highlightedSubtext"></span>
    </div>
  </ComboboxItem>
</template>

<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { ComboboxItem } from "reka-ui";
import { type Component, computed, onUnmounted, ref, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";

import Value from "@/design_system/Value";
import StripeIcon from "@/icons/brands/stripe.svg";
import ArrowRightStartOnRectangleIcon from "@/icons/heroicons/arrow-right-start-on-rectangle-micro.svg";
import ArrowTopRightOnSquareIcon from "@/icons/heroicons/arrow-top-right-on-square-micro.svg";
import BookOpenIcon from "@/icons/heroicons/book-open-micro.svg";
import ChartBarIcon from "@/icons/heroicons/chart-bar-micro.svg";
import ChatBubbleLeftRightIcon from "@/icons/heroicons/chat-bubble-left-right-micro.svg";
import ClockIcon from "@/icons/heroicons/clock-micro.svg";
import CodeBracketIcon from "@/icons/heroicons/code-bracket-micro.svg";
import DocumentTextIcon from "@/icons/heroicons/document-text-micro.svg";
import EnvelopeIcon from "@/icons/heroicons/envelope-micro.svg";
import PaperClipIcon from "@/icons/heroicons/paper-clip-micro.svg";
import NoteIcon from "@/icons/heroicons/pencil-square-micro.svg";
import QuestionMarkCircleIcon from "@/icons/heroicons/question-mark-circle-micro.svg";
import RSSIcon from "@/icons/heroicons/rss-micro.svg";
import SparklesIcon from "@/icons/heroicons/sparkles-micro.svg";
import TagIcon from "@/icons/heroicons/tag-micro.svg";
import UserIcon from "@/icons/heroicons/user-micro.svg";
import { subscriberDetailQueryOptions } from "@/lib/api/queries/subscribers/detail";
import { useNewsletterId } from "@/lib/api/queries/utils";
import { createReusableTemplate } from "@/lib/composables/template";
import { generateAction } from "@/lib/search";
import { SEARCH_CONTEXT } from "@/search/routes";
import { useStore as useUiStatesStore } from "@/store/stores/ui-states";
import type { SearchResult } from "@/types/search_result";

const queryClient = useQueryClient();
const newsletterId = useNewsletterId();

const typeIconMapping: Record<SearchResult["type"], Component> = {
  attachment: PaperClipIcon,
  automation: SparklesIcon,
  comment: ChatBubbleLeftRightIcon,
  documentation: BookOpenIcon,
  email: EnvelopeIcon,
  external_feed: RSSIcon,
  external_url: ArrowTopRightOnSquareIcon,
  historical_email: ClockIcon,
  invitation: UserIcon,
  message: ChatBubbleLeftRightIcon,
  newsletter: QuestionMarkCircleIcon,
  note: NoteIcon,
  page: ArrowTopRightOnSquareIcon,
  request: CodeBracketIcon,
  snippet: DocumentTextIcon,
  special_action: ArrowRightStartOnRectangleIcon,
  stripe_customer: StripeIcon,
  stripe_subscription: StripeIcon,
  subscriber: UserIcon,
  survey: ChartBarIcon,
  survey_response: ChartBarIcon,
  tag: TagIcon,
  webhook: CodeBracketIcon,
};

const props = defineProps<{
  item: SearchResult;
  query?: string;
  nested?: boolean;
}>();

const route = useRoute();

const isRouteActive = computed(() => {
  if (props.item.type !== "page" && props.item.type !== "special_action") {
    return false;
  }

  return route.path.startsWith(props.item.obj.name);
});

const uiStatesStore = useUiStatesStore();

const fullContext = computed(() => {
  return {
    ...SEARCH_CONTEXT,
    ...uiStatesStore.contextualSearchRoutes,
  };
});

const icon = computed(() => {
  const item = props.item;

  if (item.type == "page" || item.type == "special_action") {
    // @ts-ignore
    const page = fullContext.value[item.obj.name];
    if (page) {
      return page.icon;
    }
  }

  if (item.type == "external_url" && item.obj.icon) {
    return item.obj.icon;
  }

  return typeIconMapping[item.type];
});

const highlightedText = computed(() => {
  const text = props.item.text || "No subject";
  const query = props.query;

  if (!query || !text) {
    return text;
  }

  // Escape regex special characters in the query
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedQuery})`, "gi");

  return text.replace(
    regex,
    '<mark class="dark:bg-yellow-800 text-inherit border-b border-yellow-500 bg-yellow-200">$1</mark>'
  );
});

const highlightedMatchText = computed(() => {
  const matchText = props.item.matchText;
  const query = props.query;

  if (!query || !matchText) {
    return matchText || "";
  }

  // Escape regex special characters in the query
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedQuery})`, "gi");

  return matchText.replace(
    regex,
    '<mark class="dark:bg-yellow-800 text-inherit border-b border-yellow-500 bg-yellow-200">$1</mark>'
  );
});

const highlightedSubtext = computed(() => {
  if (props.item.type !== "note") {
    return "";
  }

  // Escape regex special characters in the query
  const escapedQuery = props.query?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedQuery})`, "gi");

  return props.item.obj.body.replace(
    regex,
    '<mark class="dark:bg-yellow-800 text-inherit border-b border-yellow-500 bg-yellow-200">$&</mark>'
  );
});

const Template = createReusableTemplate();

const action = computed(() => {
  return generateAction(props.item);
});

const itemRef = ref<{ $el: HTMLElement } | null>(null);

const prefetchSubscriber = () => {
  if (props.item.type !== "subscriber") return;

  queryClient.prefetchQuery(
    subscriberDetailQueryOptions(newsletterId.value, props.item.obj.id)
  );
};

let observer: MutationObserver | null = null;

watch(
  () => itemRef.value?.$el,
  (el) => {
    observer?.disconnect();
    if (!el || props.item.type !== "subscriber") return;

    observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-highlighted" &&
          (mutation.target as HTMLElement).hasAttribute("data-highlighted")
        ) {
          prefetchSubscriber();
        }
      }
    });
    observer.observe(el, { attributes: true });
  },
  { immediate: true }
);

onUnmounted(() => {
  observer?.disconnect();
});
</script>
